import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  CloudArrowUp,
  DownloadSimple,
  Trash,
  ArrowsCounterClockwise,
  FloppyDisk,
  Robot,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtBytes(b) {
  if (b == null) return "—";
  const u = ["B", "KB", "MB"];
  let i = 0;
  let n = Number(b);
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch {
    return s;
  }
}

export default function BackupDialog({ open, onOpenChange }) {
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState({
    enabled: false,
    interval_hours: 24,
    keep_last: 7,
  });
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [list, cfg] = await Promise.all([
        axios.get(`${API}/admin/backups`),
        axios.get(`${API}/admin/backups/config`),
      ]);
      setItems(list.data);
      setConfig(cfg.data);
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const createNow = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/admin/backups`);
      toast.success("ΔΗΜΙΟΥΡΓΗΘΗΚΕ");
      load();
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("ΔΙΑΓΡΑΦΗ ΑΥΤΟΥ ΤΟΥ BACKUP;")) return;
    try {
      await axios.delete(`${API}/admin/backups/${id}`);
      toast.success("ΔΙΑΓΡΑΦΗΚΕ");
      load();
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    }
  };

  const download = async (id, when) => {
    try {
      const res = await axios.get(`${API}/admin/backups/${id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `pinboard-backup-${(when || "").replace(/[: ]/g, "-").slice(0, 19)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    }
  };

  const restore = async (id) => {
    if (
      !window.confirm(
        "ΕΠΑΝΑΦΟΡΑ ΑΠΟ ΑΥΤΟ ΤΟ BACKUP;\n\nΟΛΑ ΤΑ ΥΠΑΡΧΟΝΤΑ POSTS/ΧΡΗΣΤΕΣ ΘΑ ΑΝΤΙΚΑΤΑΣΤΑΘΟΥΝ.\nΘΑ ΓΙΝΕΙ ΕΦΕΔΡΙΚΟ BACKUP ΠΡΩΤΑ."
      )
    )
      return;
    try {
      const { data } = await axios.post(`${API}/admin/backups/${id}/restore`);
      toast.success(`ΕΠΑΝΑΦΕΡΘΗΚΕ (${data.notes} notes, ${data.users} users)`);
      load();
      // refresh page to reload notes
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    }
  };

  const saveConfig = async (next) => {
    try {
      const { data } = await axios.put(`${API}/admin/backups/config`, next);
      setConfig(data);
      toast.success("ΑΠΟΘΗΚΕΥΤΗΚΕ");
    } catch (e) {
      toast.error("ΣΦΑΛΜΑ");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[680px] rounded-2xl p-0 overflow-hidden border-none max-h-[92vh] overflow-y-auto"
        data-testid="backup-dialog"
      >
        <div className="px-7 pt-6 pb-3 bg-ink text-white">
          <DialogHeader>
            <DialogTitle className="font-caveat text-3xl font-bold flex items-center gap-2">
              <FloppyDisk size={24} weight="bold" />
              BACKUP
            </DialogTitle>
            <DialogDescription className="text-white/70 text-[10px] uppercase tracking-[0.2em] font-bold">
              ΧΕΙΡΟΚΙΝΗΤΟ / ΑΥΤΟΜΑΤΟ / DOWNLOAD / ΕΠΑΝΑΦΟΡΑ
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-7 py-5 bg-white space-y-6">
          {/* Auto-backup config */}
          <div className="rounded-xl border border-black/10 p-3 bg-amber-50/40">
            <div className="flex items-center gap-2 mb-2">
              <Robot size={16} weight="bold" className="text-amber-700" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/70">
                ΑΥΤΟΜΑΤΟ BACKUP
              </span>
              <label className="ml-auto flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!config.enabled}
                  onChange={(e) =>
                    saveConfig({ ...config, enabled: e.target.checked })
                  }
                  data-testid="auto-backup-toggle"
                  className="w-4 h-4 accent-amber-600"
                />
                <span className="text-xs font-bold uppercase tracking-wider text-ink/80">
                  {config.enabled ? "ΕΝΕΡΓΟ" : "ΑΝΕΝΕΡΓΟ"}
                </span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-[9px] uppercase tracking-[0.18em] font-bold text-ink/55">
                ΣΥΧΝΟΤΗΤΑ (ΩΡΕΣ)
                <input
                  type="number"
                  min={1}
                  max={720}
                  value={config.interval_hours}
                  onChange={(e) =>
                    setConfig({ ...config, interval_hours: parseInt(e.target.value || "1", 10) })
                  }
                  onBlur={() => saveConfig(config)}
                  data-testid="auto-backup-interval"
                  className="block w-full mt-1 px-2 py-1.5 rounded-lg border border-ink/15 bg-white text-sm font-arial tabular-nums text-ink"
                />
              </label>
              <label className="text-[9px] uppercase tracking-[0.18em] font-bold text-ink/55">
                ΔΙΑΤΗΡΗΣΗ ΤΕΛΕΥΤΑΙΩΝ
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={config.keep_last}
                  onChange={(e) =>
                    setConfig({ ...config, keep_last: parseInt(e.target.value || "1", 10) })
                  }
                  onBlur={() => saveConfig(config)}
                  data-testid="auto-backup-keep"
                  className="block w-full mt-1 px-2 py-1.5 rounded-lg border border-ink/15 bg-white text-sm font-arial tabular-nums text-ink"
                />
              </label>
            </div>
          </div>

          {/* Manual backup */}
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ink/70">
              ΧΕΙΡΟΚΙΝΗΤΟ ({items.length} ΣΥΝΟΛΟ)
            </div>
            <button
              onClick={createNow}
              disabled={creating}
              data-testid="backup-create-now"
              className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 transition-all ${
                creating
                  ? "bg-ink/20 text-white/70 cursor-not-allowed"
                  : "bg-ink text-white hover:bg-ink/85 active:scale-95"
              }`}
            >
              <CloudArrowUp size={12} weight="bold" />
              {creating ? "ΔΗΜΙΟΥΡΓΙΑ..." : "ΝΕΟ BACKUP"}
            </button>
          </div>

          <div className="space-y-2 max-h-[44vh] overflow-y-auto">
            {loading && <p className="text-xs text-ink/50 italic">ΦΟΡΤΩΣΗ...</p>}
            {!loading && items.length === 0 && (
              <p className="text-xs text-ink/50 italic">ΧΩΡΙΣ BACKUPS</p>
            )}
            {items.map((b) => (
              <div
                key={b.id}
                data-testid={`backup-row-${b.id}`}
                className={`grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center px-3 py-2 rounded-xl border ${
                  b.kind === "auto"
                    ? "bg-amber-50/40 border-amber-200"
                    : "bg-white border-black/10"
                }`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-bold text-ink truncate font-arial">
                    {fmtDate(b.created_at)}
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-ink/55 font-bold">
                    {b.kind === "auto" ? "ΑΥΤΟΜΑΤΟ" : "ΧΕΙΡΟΚΙΝΗΤΟ"} ·{" "}
                    {b.notes_count} POSTS · {b.users_count} USERS ·{" "}
                    {fmtBytes(b.size_bytes)}
                  </div>
                </div>
                <button
                  onClick={() => download(b.id, b.created_at)}
                  data-testid={`backup-download-${b.id}`}
                  className="p-2 text-ink/70 hover:text-ink hover:bg-black/5 rounded-lg transition-colors"
                  title="ΛΗΨΗ"
                >
                  <DownloadSimple size={16} weight="bold" />
                </button>
                <button
                  onClick={() => restore(b.id)}
                  data-testid={`backup-restore-${b.id}`}
                  className="p-2 text-purple-700 hover:text-purple-800 hover:bg-purple-50 rounded-lg transition-colors"
                  title="ΕΠΑΝΑΦΟΡΑ"
                >
                  <ArrowsCounterClockwise size={16} weight="bold" />
                </button>
                <button
                  onClick={() => remove(b.id)}
                  data-testid={`backup-delete-${b.id}`}
                  className="p-2 text-red-600/70 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  title="ΔΙΑΓΡΑΦΗ"
                >
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
